import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Wallet, Search, RefreshCw, Lock, Unlock, ShieldAlert, RotateCcw, DollarSign, Sparkles, Users } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ label, value, icon: Icon, suffix }) => (
    <Card>
        <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold">{value}{suffix ? <span className="text-sm text-muted-foreground ml-1">{suffix}</span> : null}</div>
            </div>
        </CardContent>
    </Card>
);

export default function AdminWallet() {
    const [tab, setTab] = useState('wallets');
    const [overview, setOverview] = useState(null);
    const [wallets, setWallets] = useState({ items: [], total: 0 });
    const [txs, setTxs] = useState({ items: [], total: 0 });
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [txFilter, setTxFilter] = useState({ q: '', type: '', currency: '' });
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [adj, setAdj] = useState({ amount: '', currency: 'mxn', reason: '' });
    const [busy, setBusy] = useState(false);

    const loadOverview = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/wallet/overview');
            setOverview(data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    }, []);

    const loadWallets = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/wallet/wallets', { params: { q, status, limit: 100 } });
            setWallets(data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    }, [q, status]);

    const loadTxs = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/wallet/transactions', { params: { ...txFilter, limit: 200 } });
            setTxs(data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    }, [txFilter]);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { if (tab === 'wallets') loadWallets(); }, [tab, loadWallets]);
    useEffect(() => { if (tab === 'transactions') loadTxs(); }, [tab, loadTxs]);

    const doAdjust = async () => {
        if (!selectedWallet) return;
        setBusy(true);
        try {
            await api.post(`/admin/wallet/users/${selectedWallet.user_id}/adjust`, {
                amount: parseFloat(adj.amount),
                currency: adj.currency,
                reason: adj.reason,
            });
            toast.success('Ajuste aplicado');
            setAdj({ amount: '', currency: 'mxn', reason: '' });
            setSelectedWallet(null);
            loadOverview();
            loadWallets();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const doFreeze = async (userId, currentFrozen) => {
        try {
            const reason = currentFrozen ? 'Descongelamiento manual' : (prompt('Motivo de congelamiento:') || 'moderación');
            await api.post(`/admin/wallet/users/${userId}/freeze`, { frozen: !currentFrozen, reason });
            toast.success(currentFrozen ? 'Wallet descongelada' : 'Wallet congelada');
            loadWallets();
            loadOverview();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const doReverseTx = async (txId) => {
        if (!window.confirm('¿Revertir esta transacción? Se ajustará el saldo del usuario.')) return;
        try {
            await api.post(`/admin/wallet/transactions/${txId}/reverse`);
            toast.success('Transacción revertida');
            loadTxs();
            loadOverview();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-okwallet-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <Wallet className="h-6 w-6 text-[hsl(var(--primary))]" />
                        <h1 className="text-2xl font-semibold">OkWallet — Moderación</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Ajusta saldos, congela wallets y revierte transacciones sospechosas.</p>
                </div>
                <Button variant="outline" onClick={() => { loadOverview(); loadWallets(); loadTxs(); }} data-testid="admin-wallet-refresh">
                    <RefreshCw className="h-4 w-4 mr-2" />Actualizar
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Saldo total" value={`$${fmt(overview?.total_balance_mxn)}`} icon={DollarSign} suffix="MXN" />
                <StatCard label="Puntos totales" value={fmt(overview?.total_points)} icon={Sparkles} suffix="pts" />
                <StatCard label="Wallets activas" value={overview?.total_wallets ?? '—'} icon={Users} />
                <StatCard label="Congeladas" value={overview?.frozen_wallets ?? '—'} icon={Lock} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="wallets" data-testid="admin-wallet-tab-wallets">Wallets</TabsTrigger>
                    <TabsTrigger value="transactions" data-testid="admin-wallet-tab-tx">Transacciones</TabsTrigger>
                </TabsList>

                <TabsContent value="wallets" className="pt-4 space-y-3">
                    <Card>
                        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
                            <div className="relative flex-1 min-w-[240px]">
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nickname o email" className="pl-9" data-testid="admin-wallet-search" />
                            </div>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    <SelectItem value="active">Activas</SelectItem>
                                    <SelectItem value="frozen">Congeladas</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {wallets.items?.length === 0 && <div className="text-sm text-muted-foreground py-10 col-span-full text-center">Sin wallets que coincidan.</div>}
                        {wallets.items?.map(w => (
                            <Card key={w.user_id} data-testid={`admin-wallet-card-${w.nickname}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center overflow-hidden">
                                            {w.avatar_url ? <img src={w.avatar_url} alt="" className="h-full w-full object-cover" /> : <span className="font-semibold">{w.nickname?.[0]?.toUpperCase()}</span>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">@{w.nickname}</div>
                                            <div className="text-xs text-muted-foreground truncate">{w.email || '—'}</div>
                                        </div>
                                        {w.frozen && <Badge className="bg-rose-100 text-rose-800">Congelada</Badge>}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                                        <div className="p-2 rounded bg-[hsl(var(--secondary))]">
                                            <div className="text-[10px] uppercase text-muted-foreground">Saldo</div>
                                            <div className="font-semibold">${fmt(w.balance_mxn)}</div>
                                        </div>
                                        <div className="p-2 rounded bg-[hsl(var(--secondary))]">
                                            <div className="text-[10px] uppercase text-muted-foreground">Puntos</div>
                                            <div className="font-semibold">{fmt(w.points)}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setSelectedWallet(w)} data-testid={`admin-wallet-adjust-${w.nickname}`}>
                                            <ShieldAlert className="h-3.5 w-3.5 mr-1" />Ajustar
                                        </Button>
                                        <Button size="sm" variant={w.frozen ? 'default' : 'outline'} onClick={() => doFreeze(w.user_id, w.frozen)} data-testid={`admin-wallet-freeze-${w.nickname}`}>
                                            {w.frozen ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="transactions" className="pt-4 space-y-3">
                    <Card>
                        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={txFilter.q} onChange={(e) => setTxFilter({ ...txFilter, q: e.target.value })} placeholder="Buscar por nickname o descripción" className="pl-9" data-testid="admin-tx-search" />
                            </div>
                            <Select value={txFilter.type || 'all'} onValueChange={(v) => setTxFilter({ ...txFilter, type: v === 'all' ? '' : v })}>
                                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los tipos</SelectItem>
                                    <SelectItem value="topup">Recarga</SelectItem>
                                    <SelectItem value="payment">Pago</SelectItem>
                                    <SelectItem value="transfer_in">Recibido</SelectItem>
                                    <SelectItem value="transfer_out">Enviado</SelectItem>
                                    <SelectItem value="cashback">Cashback</SelectItem>
                                    <SelectItem value="admin_adjustment">Ajuste admin</SelectItem>
                                    <SelectItem value="reversal">Reversión</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={txFilter.currency || 'all'} onValueChange={(v) => setTxFilter({ ...txFilter, currency: v === 'all' ? '' : v })}>
                                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Moneda" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Cualquiera</SelectItem>
                                    <SelectItem value="mxn">MXN</SelectItem>
                                    <SelectItem value="points">Puntos</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                    <div className="rounded-lg border overflow-hidden">
                        <div className="grid grid-cols-12 text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))] px-3 py-2">
                            <div className="col-span-3">Usuario</div>
                            <div className="col-span-2">Tipo</div>
                            <div className="col-span-2">Monto</div>
                            <div className="col-span-3">Descripción</div>
                            <div className="col-span-1">Estado</div>
                            <div className="col-span-1 text-right">Acción</div>
                        </div>
                        {txs.items?.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin transacciones.</div>}
                        {txs.items?.map(t => (
                            <div key={t.id} className="grid grid-cols-12 items-center px-3 py-2 border-t text-sm hover:bg-[hsl(var(--accent))]/30" data-testid={`admin-tx-row-${t.id}`}>
                                <div className="col-span-3 truncate">
                                    <div className="font-medium truncate">@{t.user_nickname || t.user_id?.slice(0, 8)}</div>
                                    {t.counterparty_nickname && <div className="text-[10px] text-muted-foreground truncate">↔ @{t.counterparty_nickname}</div>}
                                </div>
                                <div className="col-span-2">
                                    <Badge variant="outline" className="text-[10px]">{t.type}</Badge>
                                </div>
                                <div className="col-span-2 font-semibold">{t.currency === 'points' ? `${fmt(t.amount)} pts` : `$${fmt(t.amount)}`}</div>
                                <div className="col-span-3 truncate text-xs text-muted-foreground">{t.description || '—'}</div>
                                <div className="col-span-1">
                                    <Badge className={t.status === 'reversed' ? 'bg-slate-200 text-slate-700' : t.status === 'failed' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}>{t.status}</Badge>
                                </div>
                                <div className="col-span-1 text-right">
                                    {t.status !== 'reversed' && ['topup', 'payment', 'transfer_in', 'transfer_out', 'cashback', 'refund', 'points_redeem'].includes(t.type) && (
                                        <Button size="sm" variant="ghost" onClick={() => doReverseTx(t.id)} data-testid={`admin-tx-reverse-${t.id}`} title="Revertir">
                                            <RotateCcw className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Adjust dialog */}
            <Dialog open={!!selectedWallet} onOpenChange={(v) => !v && setSelectedWallet(null)}>
                <DialogContent data-testid="admin-wallet-adjust-dialog">
                    <DialogHeader>
                        <DialogTitle>Ajustar wallet de @{selectedWallet?.nickname}</DialogTitle>
                    </DialogHeader>
                    {selectedWallet && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))] text-sm">
                                <div><div className="text-[10px] uppercase text-muted-foreground">Saldo actual</div><div className="font-semibold">${fmt(selectedWallet.balance_mxn)}</div></div>
                                <div><div className="text-[10px] uppercase text-muted-foreground">Puntos</div><div className="font-semibold">{fmt(selectedWallet.points)}</div></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Monto (positivo o negativo)</Label>
                                    <Input type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="100 o -50" data-testid="admin-adjust-amount" />
                                </div>
                                <div>
                                    <Label>Moneda</Label>
                                    <Select value={adj.currency} onValueChange={(v) => setAdj({ ...adj, currency: v })}>
                                        <SelectTrigger data-testid="admin-adjust-currency"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="mxn">Saldo MXN</SelectItem>
                                            <SelectItem value="points">Puntos</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label>Motivo del ajuste</Label>
                                <Textarea rows={3} value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Ej: compensación por incidencia con orden #123" data-testid="admin-adjust-reason" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setSelectedWallet(null)}>Cancelar</Button>
                        <Button onClick={doAdjust} disabled={busy || !adj.amount || !adj.reason} data-testid="admin-adjust-confirm">{busy ? 'Guardando…' : 'Aplicar ajuste'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
